import { Sidebar } from '$components'
import { Outlet } from 'react-router'
// import { RoutePath } from 'src/enums/routing'
import { cn } from '$components'
import { CalendarFilledIcon, CheckDoneIcon, DogIcon, FolderTagIcon, LogoIcon, PersonCircleIcon, SettingsIcon, SmileIcon, InboxIcon } from '$components'
import { MobileMenu } from '$components'
import { ComponentType, Fragment } from 'react'
import { NavigationLink } from '$components'
import { ExpandableNavigationLink } from '$components'
import { PetRoutePaths } from '$features/pets/routes/protected'
import { CalendarRoutePaths } from '$features/calendar/routes/protected'
import { ClientRoutePath } from '$features/clients/routes/protected'
import { AppointmentRoutePath } from '$features/appointments/routes/protected'
import { ServiceRoutePath } from '$features/services/routes/protected'
import { ActiveUser } from '$components'
import { SettingsRoutePath } from '$features/settings/routes/protected'
import { TeamRoutePath } from '$features/team/routes/protected'
import { SubscriptionExpiryBanner } from '$components'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { OnboardingTutorial, DEFAULT_ONBOARDING_WIZARD_CONFIG } from '$features/onboarding-tutorial'
import { useAuth } from '$features/auth/hooks/use-auth'
import { Role } from '$api-client/types.gen'
import { Config } from '$config/app'
import { useModalState } from '$components'
import { SubmitSupportTicketModal } from '$features/support/components/submit-support-ticket-modal'
import { SupportLink } from '$features/support/components/support-link'

export type NavigationLink = {
  to: string
  icon: ComponentType
  children: string,
}

const links: NavigationLink[]  = [
  // {
  //   to: RoutePath.Root,
  //   icon: GridIcon,
  //   children: 'Dashboard',
  // },
  {
    to: CalendarRoutePaths.Calendar,
    icon: CalendarFilledIcon,
    children: 'Calendar',
  },
  {
    to: AppointmentRoutePath.Appointments,
    icon: CheckDoneIcon,
    children: 'Appointments',
  },
  {
    to: AppointmentRoutePath.AppointmentsRequests,
    icon: InboxIcon,
    children: 'Appointment Requests',
  },
  {
    to: ClientRoutePath.Clients,
    icon: SmileIcon,
    children: 'Clients',
  },
  {
    to: PetRoutePaths.Pets,
    icon: DogIcon,
    children: 'Pets',
  },
  {
    to: ServiceRoutePath.Services,
    icon: FolderTagIcon,
    children: 'Services',
  },
  {
    to: TeamRoutePath.Team,
    icon: PersonCircleIcon,
    children: 'Team Members',
  },
  {
    to: SettingsRoutePath.Settings,
    icon: SettingsIcon,
    children: 'Settings',
  },
]

function useSettingsSubLinks() {
  const { user } = useAuth()

  const subLinks = []

  // Organisation links (for admins only)
  if (user?.role === Role.SUPER_ADMIN || user?.role === Role.ADMIN) {
    subLinks.push({
      to: SettingsRoutePath.Settings + '/' + SettingsRoutePath.SettingsGeneral,
      children: 'General',
    })

    // Billing link (for super admins only)
    if (user?.role === Role.SUPER_ADMIN) {
      subLinks.push({
        to: Config.getStripeBillingUrl(user.email),
        children: 'Billing',
      })
    }
  }

  // Personal links (for all users)
  subLinks.push({
    to: SettingsRoutePath.Settings + '/' + SettingsRoutePath.SettingsProfile,
    children: 'Profile',
  })

  return subLinks
}

export function DashboardLayout() {
  const { hasSubscriptionExpired } = useTenant()
  const settingsSubLinks = useSettingsSubLinks()
  const { modalState: supportModalState, open: openSupportModal, close: closeSupportModal } = useModalState()

  return (
    <div className={cn(
      'grid grid-cols-1 h-dvh overflow-hidden bg-surface-muted',
      'lg:grid-cols-[min-content_1fr]',
    )}
    >
      <div className="hidden lg:block">
        <Sidebar>
          {({ isOpen }) => (
            <Fragment>
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex-shrink-0">
                  { isOpen
                    ? (
                      <div className="flex justify-center items-center">
                        <LogoIcon className="h-8 w-fit pl-stack" />

                        <Sidebar.ResizeButton />
                      </div>
                    )
                    : <Sidebar.ResizeButton />
                  }
                </div>

                {/* Navigation Links */}
                <div className="flex-1 overflow-y-auto">
                  <div className="flex flex-col px-stack-tight py-stack-dense gap-stack-tight">
                    {links.map((link, index) => (
                      <Sidebar.Link
                        key={index}
                        to={link.to}
                        icon={link.icon}
                      >
                        {link.children}
                      </Sidebar.Link>
                    ))}

                    <SupportLink onClick={() => openSupportModal(-1)} />
                  </div>
                </div>

                <div className="px-stack-tight pb-stack-tight">
                  <OnboardingTutorial config={DEFAULT_ONBOARDING_WIZARD_CONFIG} />
                </div>
              </div>
            </Fragment>
          )}
        </Sidebar>
      </div>

      <div className="h-dvh lg:pl-0 lg:p-stack-tight bg-surface-muted">
        <div className={cn(
          'lg:border lg:rounded-card-sm lg:border-border h-full grid bg-surface',
          {
            'grid-rows-[min-content_1fr]': !hasSubscriptionExpired,
            'grid-rows-[min-content_min-content_1fr]': hasSubscriptionExpired,
          },
        )}
        >
          <div className="p-stack-tight border-b border-border flex justify-between items-center">
            <div className="lg:hidden">
              <MobileMenu>
                { (close) => (
                  <div className="flex flex-col gap-stack-tight">
                    {links.map((link, index) => {
                      // Use expandable navigation for settings
                      if (link.to === SettingsRoutePath.Settings) {
                        return (
                          <ExpandableNavigationLink
                            key={index}
                            to={link.to}
                            onClick={close}
                            icon={link.icon}
                            subLinks={settingsSubLinks}
                          >
                            {link.children}
                          </ExpandableNavigationLink>
                        )
                      }

                      // Use regular navigation for other links
                      return (
                        <NavigationLink
                          key={index}
                          to={link.to}
                          onClick={close}
                          icon={link.icon}
                        >
                          {link.children}
                        </NavigationLink>
                      )
                    })}

                    <SupportLink onClick={() => {
                      openSupportModal(-1)
                      close()
                    }}
                    />
                  </div>
                )}
              </MobileMenu>
            </div>

            <div className="ml-auto">
              <ActiveUser onOpenSupport={() => openSupportModal(-1)} />
            </div>
          </div>

          {supportModalState.isOpen && <SubmitSupportTicketModal onClose={closeSupportModal} />}

          <SubscriptionExpiryBanner />

          <div className="overflow-y-auto">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  )
}
